/**
 * RBAC-8.2 · FR-29, FR-33. Recent changes: who changed which role or whose access, and when.
 *
 * The third tab on Roles & Permissions (`?view=changes`). Renders `describeEvent()` and nothing else.
 * Four filters, the dimensions Slack's audit log uses: date range, acting user, who or what was
 * affected (one picker over people and roles), and kind of change. "Administration given or taken
 * away" is a kind of its own and has a one-click shortcut, because those are the changes an admin is
 * most likely to be hunting for and must never have to find by scrolling.
 *
 * Newest first, 50 at a time, "Show older changes" for the next page (keyset on id, so nothing is
 * skipped or repeated when new changes land while reading).
 */
import { useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Info, Loader2, ShieldCheck, ShieldOff, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { cn } from '@/lib/utils';
import {
  NO_FILTERS,
  ROLE_AUDIT_KINDS,
  actorExplanation,
  affectedValue,
  changesCount,
  describeEvent,
  emptyText,
  isFiltered,
  parseAffectedValue,
  rangeProblem,
  type RoleAuditEvent,
  type RoleAuditFilters,
  type RoleAuditKind,
} from '@/lib/roleAudit';
import { defaultDateFormat } from '@/lib/roleHistory';
import { fetchRoleAuditFacets, fetchRoleAuditPage } from '@/services/roleAuditService';

export const roleAuditQueryKey = (tenantId: string | null | undefined) => ['rbac', 'role-audit', tenantId] as const;

const ANY = '';

function Row({ event }: { event: RoleAuditEvent }) {
  const line = describeEvent(event);
  const why = actorExplanation(event);
  const Shield = line.tone === 'loss' ? ShieldOff : ShieldCheck;
  return (
    <li
      className={cn(
        'flex flex-col gap-1 border-b border-border px-4 py-3 last:border-b-0 sm:flex-row sm:items-start sm:justify-between sm:gap-4',
        line.administration && 'border-l-4 border-l-amber-500 bg-amber-50/60 dark:bg-amber-950/30',
      )}
      data-event-id={event.id}
      data-event-type={event.type}
    >
      <div className="min-w-0 space-y-0.5">
        {line.administration && (
          <p className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-amber-800 dark:text-amber-300">
            <Shield className="h-3.5 w-3.5" aria-hidden />
            Administration {line.tone === 'loss' ? 'taken away' : 'given'}
          </p>
        )}
        <p className="break-words text-sm font-medium text-foreground">{line.summary}</p>
        {line.detail && <p className="break-words text-sm text-muted-foreground">{line.detail}</p>}
        {why && <p className="text-xs text-muted-foreground">{why}</p>}
      </div>
      <time className="shrink-0 text-xs text-muted-foreground sm:pt-0.5" dateTime={event.occurredAt}>
        {defaultDateFormat(event.occurredAt)}
      </time>
    </li>
  );
}

export function RoleChangeLog({ tenantId }: { tenantId: string | null | undefined }) {
  const [filters, setFilters] = useState<RoleAuditFilters>(NO_FILTERS);
  const set = (patch: Partial<RoleAuditFilters>) => setFilters((f) => ({ ...f, ...patch }));
  const problem = rangeProblem(filters);

  const facets = useQuery({
    queryKey: [...roleAuditQueryKey(tenantId), 'facets'],
    queryFn: fetchRoleAuditFacets,
    enabled: !!tenantId,
    staleTime: 30 * 1000,
  });

  const log = useInfiniteQuery({
    queryKey: [...roleAuditQueryKey(tenantId), 'log', filters],
    queryFn: ({ pageParam }) => fetchRoleAuditPage(filters, pageParam),
    initialPageParam: null as number | null,
    getNextPageParam: (last) => last.nextBeforeId,
    enabled: !!tenantId && !problem,
    staleTime: 30 * 1000,
  });

  const events = useMemo(() => log.data?.pages.flatMap((p) => p.events) ?? [], [log.data]);

  const actorOptions = useMemo(
    () => [{ value: ANY, label: 'Anyone' }, ...(facets.data?.actors ?? []).map((a) => ({ value: a.id, label: a.label, description: changesCount(a.count) }))],
    [facets.data],
  );
  const affectedOptions = useMemo(
    () => [
      { value: ANY, label: 'Anyone or any role' },
      ...(facets.data?.people ?? []).map((p) => ({ value: affectedValue({ kind: 'person', id: p.id }), label: p.label, description: `Person · ${changesCount(p.count)}` })),
      ...(facets.data?.roles ?? []).map((r) => ({ value: affectedValue({ kind: 'role', id: r.id }), label: r.label, description: `Role · ${changesCount(r.count)}` })),
    ],
    [facets.data],
  );

  const adminOnly = filters.kind === 'administration';

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold text-foreground">Recent changes</h2>
        <p className="text-sm text-muted-foreground">
          Who created, changed or deleted a role, who gave or took away a role, and whose access was removed. Newest first.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant={adminOnly ? 'default' : 'outline'}
          aria-pressed={adminOnly}
          onClick={() => set({ kind: adminOnly ? null : 'administration' })}
        >
          <ShieldCheck className="mr-1.5 h-4 w-4" />
          Administration changes only
          {facets.data && (
            <span className="ml-1.5 tabular-nums opacity-80">
              {(facets.data.types.administration_granted ?? 0) + (facets.data.types.administration_removed ?? 0)}
            </span>
          )}
        </Button>
        {isFiltered(filters) && (
          <Button size="sm" variant="ghost" onClick={() => setFilters(NO_FILTERS)}>
            <X className="mr-1 h-4 w-4" />
            Clear filters
          </Button>
        )}
      </div>

      {/* min-w-0 + grid-cols-1: a fieldset defaults to min-inline-size: min-content and an implicit grid column to
          auto, so two native date inputs pushed the box past a 390px screen (seen in the RBAC-8.2 drive). */}
      <fieldset className="grid min-w-0 grid-cols-1 gap-3 rounded-xl border border-border bg-card p-4 sm:grid-cols-2 lg:grid-cols-4" aria-label="Filter changes">
        <div className="min-w-0 space-y-1.5">
          <Label htmlFor="rbac-audit-from">Date range</Label>
          <div className="flex items-center gap-2">
            <Input
              id="rbac-audit-from"
              type="date"
              aria-label="From"
              value={filters.fromDay ?? ''}
              max={filters.toDay ?? undefined}
              onChange={(e) => set({ fromDay: e.target.value || null })}
              className="min-w-0"
            />
            <span className="text-sm text-muted-foreground">to</span>
            <Input
              type="date"
              aria-label="To"
              value={filters.toDay ?? ''}
              min={filters.fromDay ?? undefined}
              onChange={(e) => set({ toDay: e.target.value || null })}
              className="min-w-0"
            />
          </div>
        </div>
        <div className="min-w-0 space-y-1.5">
          <Label htmlFor="rbac-audit-actor">Changed by</Label>
          <SearchableSelect
            id="rbac-audit-actor"
            options={actorOptions}
            value={filters.actorId ?? ANY}
            onValueChange={(v) => set({ actorId: v || null })}
            placeholder="Anyone"
            disabled={!facets.data}
          />
        </div>
        <div className="min-w-0 space-y-1.5">
          <Label htmlFor="rbac-audit-affected">Person or role affected</Label>
          <SearchableSelect
            id="rbac-audit-affected"
            options={affectedOptions}
            value={affectedValue(filters.affected)}
            onValueChange={(v) => set({ affected: parseAffectedValue(v) })}
            placeholder="Anyone or any role"
            disabled={!facets.data}
          />
        </div>
        <div className="min-w-0 space-y-1.5">
          <Label htmlFor="rbac-audit-kind">Kind of change</Label>
          <select
            id="rbac-audit-kind"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
            value={filters.kind ?? ANY}
            onChange={(e) => set({ kind: (e.target.value || null) as RoleAuditKind | null })}
          >
            <option value={ANY}>All changes</option>
            {ROLE_AUDIT_KINDS.map((k) => (
              <option key={k.id} value={k.id}>
                {k.label}
              </option>
            ))}
          </select>
        </div>
      </fieldset>

      {problem && (
        <p role="alert" className="text-sm text-destructive">
          {problem}
        </p>
      )}

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        {log.isLoading || (!tenantId && !problem) ? (
          <div className="flex h-32 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : log.error ? (
          <p className="p-4 text-sm text-destructive">
            The changes could not be loaded. {log.error instanceof Error ? log.error.message : ''}
          </p>
        ) : events.length === 0 ? (
          <p className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
            <Info className="h-4 w-4 shrink-0" aria-hidden />
            {emptyText(filters, facets.data ?? null)}
          </p>
        ) : (
          <ul aria-label="Changes, newest first">
            {events.map((e) => (
              <Row key={e.id} event={e} />
            ))}
          </ul>
        )}
      </div>

      {log.hasNextPage && (
        <div className="flex justify-center">
          <Button variant="outline" size="sm" onClick={() => void log.fetchNextPage()} disabled={log.isFetchingNextPage}>
            {log.isFetchingNextPage && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Show older changes
          </Button>
        </div>
      )}
    </div>
  );
}
